import axios from "axios";
import { SERVER_ENDPOINT } from "../config";
import { getAccessToken, clearSession, markSessionExpired } from "./authApi";

const BASE = SERVER_ENDPOINT;

// Dedicated instance (not the global axios object) so the Bearer token only
// ever goes to our own backend — geocodeApi.js talks to a third-party host
// and must never see it.
const http = axios.create({ baseURL: BASE });
http.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
// The 20-minute access token has no refresh — once it expires, every
// authenticated call 401s. Rather than leave the applicant stuck on a wizard
// that silently stops saving, end the session and bounce them back to
// login. A full reload (instead of just flipping component state) resets
// every component's in-memory data cleanly, App.vue included.
http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      clearSession();
      markSessionExpired();
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

/**
 * The lookup and upload endpoints below already exist in production and are
 * reused as-is (same calls SimpleForm.vue makes).
 */

export function getProvinces() {
  return http.get(`/provinces`);
}

export function getCantons(provinceId) {
  return http.get(`/cantons?provinceId=${provinceId}`);
}

export function getDistricts(provinceId, cantonId) {
  return http.get(`/districts?provinceId=${provinceId}&cantonId=${cantonId}`);
}

export function getRolePositions() {
  return http.get(`/rolePositions`);
}

export function getPerson(govId) {
  return http.get(`/persons/${govId}`);
}

export function uploadFile(file) {
  const formData = new FormData();
  formData.append("file", file);
  return http.post(`/uploadFile`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

/**
 * Best-effort OCR of a cédula's or driver's license's expiration date, run alongside uploadFile()
 * on the same file. Always resolves 200 with { expiration_date: "YYYY-MM-DD" }
 * or { expiration_date: null } when it can't read the date (illegible scan,
 * service down, etc.) — callers should treat both as non-fatal.
 */
export function extractDocumentExpiration(file) {
  const formData = new FormData();
  formData.append("file", file);
  return http.post(`/extractDocumentExpiration`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

/**
 * Same contract as extractDocumentExpiration(), but for the hoja de
 * delincuencia (Certificación de Antecedentes Penales): the backend uses a
 * prompt specific to that document, which reads the issue date written out
 * in words plus its "VIGENCIA POR ..." period and returns the resulting
 * expiration date.
 */
export function extractCriminalRecordExpiration(file) {
  const formData = new FormData();
  formData.append("file", file);
  return http.post(`/extractCriminalRecordExpiration`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

/**
 * Spaces-stored files are private — uploadFile() only returns the storage
 * key (e.g. "rrhh/1743647507-a1b2c3d4e5.jpg"), not a usable link. This
 * exchanges that key for a temporary (1h) signed URL, used to render an
 * actual <img> preview (profile photo, signature) in the wizard.
 */
export function getFileUrl(prefix) {
  return http.get(`/fileUrl`, { params: { prefix } });
}

/**
 * Creates the candidate on the first call for a given gov_id, and updates
 * that same record on every call after that — used both for the per-step
 * progress saves and the final submit (see NewApplicationForm.vue).
 */
export function saveCandidate(payload) {
  return http.post(`/candidate`, payload);
}

/**
 * Autosave in the portal of a section the applicant already completed ("personal", "documents" or "residence"):
 * only the fields that changed, shaped as saveCandidate() sends them (lists whole). The backend merges them with what
 * is saved and validates the section; resolves with the candidate as GET /candidate/me returns it.
 */
export function patchCandidateSection(section, fields) {
  return http.patch(`/candidate/me/record/${section}`, fields);
}

/**
 * Looks up a previously saved application by gov_id, so a returning
 * applicant can have their progress reloaded into the wizard.
 */
export function getCandidateDraft(govId) {
  return http.get(`/candidate/lookup/${encodeURIComponent(govId)}`);
}

/**
 * Looks up the candidate application tied to the logged-in account
 * (identified via the Bearer token, not gov_id) — used right after login
 * to reload progress without the applicant re-entering their cédula.
 */
export function getMyCandidateDraft() {
  return http.get(`/candidate/me`);
}
